import exifr from "exifr";

export async function extractPhotoMetadata(file) {
  const extractedAt = new Date().toISOString();
  let tags = {};

  try {
    // Ask exifr for every metadata segment we use below. Missing or unreadable
    // segments are fine; the returned tags are normalized before submission.
    tags =
      (await exifr.parse(file, {
        jfif: true,
        ihdr: true,
        iptc: true,
        tiff: true,
        xmp: true,
      })) || {};
  } catch (error) {
    console.error("Unable to extract photo metadata: " + error);
  }

  return compactObject({
    file: compactObject({
      name: file.name,
      type: file.type,
      size: file.size,
      last_modified_at: isoString(file.lastModified ? new Date(file.lastModified) : null),
    }),
    camera: compactObject({
      make: tags?.Make,
      model: tags?.Model,
      software: tags?.Software,
      lens_model: tags?.LensModel,
    }),
    image: compactObject({
      width: firstPresent(tags?.ExifImageWidth, tags?.ImageWidth, tags?.PixelXDimension),
      height: firstPresent(tags?.ExifImageHeight, tags?.ImageHeight, tags?.PixelYDimension),
      orientation: tags?.Orientation,
      color_space: tags?.ColorSpace,
    }),
    timestamps: compactObject({
      captured_at: isoString(firstPresent(tags?.DateTimeOriginal, tags?.CreateDate, tags?.DateCreated)),
      digitized_at: isoString(tags?.DateTimeDigitized),
      modified_at: isoString(tags?.ModifyDate),
      file_last_modified_at: isoString(file.lastModified ? new Date(file.lastModified) : null),
      extracted_at: extractedAt,
    }),
    gps: compactObject({
      latitude: tags?.latitude,
      longitude: tags?.longitude,
      altitude: firstPresent(tags?.GPSAltitude, tags?.altitude),
      accuracy: tags?.GPSHPositioningError,
      speed: tags?.GPSSpeed,
      direction: firstPresent(tags?.GPSImgDirection, tags?.GPSTrack),
    }),
    exposure: compactObject({
      iso: tags?.ISO,
      f_number: tags?.FNumber,
      exposure_time: tags?.ExposureTime,
      focal_length: tags?.FocalLength,
    }),
  });
}

export function photoMetadataCapturedAt(metadata) {
  return metadata?.timestamps?.captured_at || metadata?.timestamps?.file_last_modified_at || "";
}

export function photoMetadataLatitude(metadata) {
  return metadata?.gps?.latitude ?? "";
}

export function photoMetadataLongitude(metadata) {
  return metadata?.gps?.longitude ?? "";
}

export function photoMetadataAltitude(metadata) {
  return metadata?.gps?.altitude ?? "";
}

export function photoMetadataAccuracy(metadata) {
  return metadata?.gps?.accuracy ?? "";
}

function firstPresent(...values) {
  // Some cameras and encoders write the same concept under different EXIF/XMP
  // tag names, so use the first populated value from the known alternatives.
  return values.find((value) => value !== undefined && value !== null && value !== "");
}

function isoString(value) {
  if (!value) return "";

  // exifr may return Date objects or date-like strings depending on the tag.
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
}

function compactObject(object) {
  // Keep the submitted JSON concise by removing blank values and sections that
  // became empty after their blank fields were removed.
  return Object.fromEntries(
    Object.entries(object).filter(([, value]) => {
      if (value === undefined || value === null || value === "") return false;
      if (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0) return false;

      return true;
    }),
  );
}
